import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

const AdminFacilities = () => {
  const [facilities, setFacilities] = useState([
    { id: 1, name: "Laptop", description: "Company issued laptop" },
    { id: 2, name: "Medical Allowance", description: "Monthly medical claim" },
  ]);
  const [requests, setRequests] = useState([
    { id: 1, employee: "John Doe", facility: "Laptop", reason: "New project", status: "Pending" },
    { id: 2, employee: "Jane Smith", facility: "Medical Allowance", reason: "Treatment", status: "Recommended" },
  ]);
  const [policies, setPolicies] = useState([
    { id: 1, title: "Laptop Usage", description: "Only for official use." },
  ]);

  const [newFacility, setNewFacility] = useState("");
  const [facilityDesc, setFacilityDesc] = useState("");
  const [policyTitle, setPolicyTitle] = useState("");
  const [policyDesc, setPolicyDesc] = useState("");
  const navigate = useNavigate();


  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token"); // or sessionStorage
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  // Load Bootstrap dynamically
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    bootstrapLink.id = "adminfacilities-bootstrap";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    iconsLink.id = "adminfacilities-icons";
    document.head.appendChild(iconsLink);

    return () => {
      const existingBootstrap = document.getElementById("adminfacilities-bootstrap");
      if (existingBootstrap) existingBootstrap.remove();

      const existingIcons = document.getElementById("adminfacilities-icons");
      if (existingIcons) existingIcons.remove();
    };
  }, []);

  // Handlers
  const handleAddFacility = (e) => {
    e.preventDefault();
    const newF = { id: Date.now(), name: newFacility, description: facilityDesc };
    setFacilities([...facilities, newF]);
    setNewFacility("");
    setFacilityDesc("");
  };

  const handleAddPolicy = (e) => {
    e.preventDefault();
    const newP = { id: Date.now(), title: policyTitle, description: policyDesc };
    setPolicies([...policies, newP]);
    setPolicyTitle("");
    setPolicyDesc("");
  };

  const handleApprove = (id) => {
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: "Approved" } : r)));
  };

  const handleReject = (id) => {
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: "Rejected" } : r)));
  };

  const getBadge = (status) => {
    if (status === "Approved") return <span className="badge bg-success">{status}</span>;
    if (status === "Rejected") return <span className="badge bg-danger">{status}</span>;
    if (status === "Recommended") return <span className="badge bg-info">{status}</span>;
    return <span className="badge bg-warning text-dark">{status}</span>;
  };

  return (
    <div className="container mt-4">
      <h2 className="fw-bold text-center text-danger mb-4">
        🛠️ Admin Facilities Management
      </h2>

      {/* Manage Facilities */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-primary text-white fw-bold">
          ➕ Manage Facilities
        </div>
        <div className="card-body">
          <form className="row g-3 mb-3" onSubmit={handleAddFacility}>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Facility name"
                value={newFacility}
                onChange={(e) => setNewFacility(e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Description"
                value={facilityDesc}
                onChange={(e) => setFacilityDesc(e.target.value)}
                required
              />
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-success w-100">
                <i className="bi bi-plus-circle"></i> Add
              </button>
            </div>
          </form>

          <ul className="list-group">
            {facilities.map((f) => (
              <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <strong>{f.name}</strong> - {f.description}
                </div>
                <button className="btn btn-sm btn-outline-danger">
                  <i className="bi bi-trash"></i>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Requests Approval */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-warning fw-bold">
          📋 Approve / Reject Requests
        </div>
        <div className="card-body">
          <table className="table table-hover text-center align-middle">
            <thead className="table-light">
              <tr>
                <th>Employee</th>
                <th>Facility</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id}>
                  <td>{req.employee}</td>
                  <td>{req.facility}</td>
                  <td>{req.reason}</td>
                  <td>{getBadge(req.status)}</td>
                  <td>
                    {req.status === "Pending" || req.status === "Recommended" ? (
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleApprove(req.id)}
                        >
                          <i className="bi bi-check-circle"></i> Approve
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleReject(req.id)}
                        >
                          <i className="bi bi-x-circle"></i> Reject
                        </button>
                      </div>
                    ) : (
                      <i className="bi bi-check2-circle text-success fs-5"></i>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Publish Policies */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-dark text-white fw-bold">
          📑 Publish Policies
        </div>
        <div className="card-body">
          <form className="row g-3 mb-3" onSubmit={handleAddPolicy}>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Policy Title"
                value={policyTitle}
                onChange={(e) => setPolicyTitle(e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Description"
                value={policyDesc}
                onChange={(e) => setPolicyDesc(e.target.value)}
                required
              />
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-primary w-100">
                <i className="bi bi-upload"></i> Publish
              </button>
            </div>
          </form>

          <ul className="list-group">
            {policies.map((p) => (
              <li key={p.id} className="list-group-item">
                <strong>{p.title}</strong>: {p.description}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Reports Section */}
      <div className="card shadow-sm">
        <div className="card-header bg-success text-white fw-bold">
          📊 Generate Reports
        </div>
        <div className="card-body">
          <button className="btn btn-outline-primary me-2">
            <i className="bi bi-file-earmark-pdf"></i> Export PDF
          </button>
          <button className="btn btn-outline-success">
            <i className="bi bi-file-earmark-excel"></i> Export Excel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminFacilities;
